import {formatMoney} from '@shopify/theme-currency';
import serialize from 'form-serialize';

interface ProductData {
  options: string[];
  variants: ProductVariant[];
}

interface OptionValuePair {
  name: string;
  value: string;
}

interface ProductVariant {
  available: boolean;
  id: number;
  price: number;
  options: string[];
  option1: string;
  option2: string;
  option3: string;
}

export interface ProductFormOptions {
  needSelection: string;
  optionGroupSelector: string;
  optionSelector: string;
  priceSelector: string;
  selectSelector: string;
  submitSelector: string;
  productData: ProductData;
}

type Options = Partial<ProductFormOptions>;

export class ProductForm {
  static defaults = {
    needSelection: 'need-selection',
    optionGroupSelector: '[data-options-parent]',
    optionSelector: '[data-option]',
    priceSelector: '[data-product-price]',
    productData: {variants: [], options: []},
    selectSelector: '[data-option-select]',
    submitSelector: '[data-ajaxadd-button]',
  };
  container: HTMLFormElement;
  currentSelection: string;
  select: HTMLSelectElement;
  optionElements: HTMLInputElement[];
  optionGroup: HTMLFieldSetElement;
  options: ProductFormOptions;
  optionNames: string[];
  priceElement: HTMLElement;
  submitElement: HTMLFormElement;

  constructor(container: HTMLFormElement, options: Options) {
    if (!container) {
      return;
    }

    this.container = container;
    this.options = {
      ...ProductForm.defaults,
      ...this.container.dataset,
      ...options,
    };

    const priceElement = container.querySelector(
      this.options.priceSelector,
    ) as HTMLElement;

    if (priceElement) {
      this.priceElement = priceElement;
    }

    const selector = container.querySelector(
      this.options.selectSelector,
    ) as HTMLSelectElement;

    if (!selector || !this.options.productData) {
      return;
    }

    const submitElement = container.querySelector(
      this.options.submitSelector,
    ) as HTMLFormElement;

    if (submitElement) {
      this.submitElement = submitElement;
    }

    const optionGroup = container.querySelector(
      this.options.optionGroupSelector,
    ) as HTMLFieldSetElement;

    if (optionGroup) {
      this.optionGroup = optionGroup;
    }

    this.select = selector;
    this.optionNames = this.options.productData.options;
    this.optionElements = Array.from(
      this.container.querySelectorAll(this.options.optionSelector),
    );

    this.bind();
    if (this.hasOptions) {
      this.disableUnavailable();
    }
  }

  get hasOptions() {
    return this.options.productData.variants.length > 1;
  }

  bind() {
    this.container.addEventListener('change', this.handleChange.bind(this));
    if (this.hasOptions) {
      this.submitElement.addEventListener(
        'click',
        this.checkSelected.bind(this),
      );
    }
  }

  handleChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const matches = target.closest(this.options.optionSelector);

    if (!matches) {
      return;
    }

    const variant = this.getVariant();
    this.disableUnavailable(target.value, variant);
  }

  getCurrentOptions() {
    const formData = serialize(this.container, {
      hash: true,
    });

    return Object.values(formData.options || []);
  }

  getVariant(): ProductVariant | undefined {
    const values = this.getCurrentOptions();
    return getVariantByOptions(values, this.options.productData);
  }

  setVariant(variant: ProductVariant) {
    const {id = ''} = variant;

    this.select.value = id.toString();
    this.currentSelection = id.toString();

    this.setPrice(variant.price);

    const event = new CustomEvent('optionsChange', {
      bubbles: true,
      detail: {
        variant,
      },
    });

    this.container.dispatchEvent(event);
    this.optionGroup.classList.remove(this.options.needSelection);
  }

  checkSelected(e: Event) {
    if (this.currentSelection === '') {
      e.preventDefault();
      this.optionGroup.classList.add(this.options.needSelection);
    }
  }

  setPrice(price: number) {
    const target = this.priceElement;

    if (!target) {
      return;
    }

    this.priceElement.innerHTML = formatMoney(
      price,
      target.dataset.moneyFormat,
    );
  }

  getVariantMap(): OptionValuePair[][] {
    return this.options.productData.variants.map(product =>
      this.optionNames.map((name, index) => {
        return {
          name: `options[${name}]`,
          value: product.options[index],
        };
      }),
    );
  }

  getClosestAvailableVariant(
    option: string,
    currentVariant?: ProductVariant,
  ): ProductVariant | undefined {
    return this.options.productData.variants.find(product => {
      const options = product.options;
      const isSameVariant = options.every(val => {
        if (!currentVariant) {
          return false;
        }

        return currentVariant.options.includes(val);
      });

      if (!options.includes(option) || isSameVariant || !product.available) {
        return false;
      }

      return true;
    });
  }

  setProductOptions(options: string[]) {
    const targets = this.optionElements.filter(opt =>
      options.includes(opt.value),
    );

    // Handle input type
    targets.forEach(element => (element.checked = true));
  }

  /**
   * Returns an array of option names
   */
  getOptionsToDisable(option: string) {
    // Find every combination with the given value
    return this.options.productData.variants.map(product => {
      const options = product.options;

      if (!options.includes(option)) {
        return;
      }

      return product;
    });
  }

  disableUnavailable = (
    value?: string,
    variant: ProductVariant | undefined = this.getVariant(),
  ) => {
    this.currentSelection = '';

    const unavailableVariants = this.options.productData.variants.filter(
      productVariant => {
        return !productVariant.available && productVariant.options.length === 1;
      },
    );

    if (!!unavailableVariants) {
      unavailableVariants.forEach(unavailableVariant => {
        this.optionElements.forEach(optionElement => {
          if (!optionElement.disabled) {
            optionElement.disabled =
              optionElement.value === unavailableVariant.options[0];
          }
        });
      });

      if (
        unavailableVariants.length === this.options.productData.variants.length
      ) {
        return;
      }
    }

    const selectedOption = value || (variant && variant.option1);

    if (!selectedOption) {
      return;
    }

    // -> Get all option data
    // -> on change: .some(variants, selected option + each combination of options)
    // disable matches, enable unmatches, if disabled match is selected -> select next value

    if (!variant || !variant.available) {
      const closest = this.getClosestAvailableVariant(selectedOption, variant);

      // No closest variant available
      if (!closest) {
        return;
      }

      this.setVariant(closest);
      this.setProductOptions(closest.options);
      return;
    }

    this.setVariant(variant);
  };
}

const getVariantByOptions = (
  values: any,
  data: ProductData,
): ProductVariant | undefined => {
  const {variants = []} = data;

  return variants.find((variant: ProductVariant) =>
    variant.options.every(opt => values.includes(opt)),
  );
};
